import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { X, Link as LinkIcon, Upload, Image as ImageIcon } from 'lucide-react';
import { useDialogA11y } from '../ui/useDialogA11y';

interface MaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  materialId?: string;
}

export function MaterialModal({ isOpen, onClose, onSuccess, materialId }: MaterialModalProps) {
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_type: 'video',
    file_url: '',
    thumbnail_url: '',
    category: 'Videos',
    embed_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const [useUrl, setUseUrl] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (materialId && isOpen) {
      loadMaterial();
    } else {
      resetForm();
    }
  }, [materialId, isOpen]);

  const loadMaterial = async () => {
    try {
      const { data, error } = await supabase
        .from('materials')
        .select('*')
        .eq('id', materialId)
        .single();

      if (error) throw error;
      if (data) {
        setFormData({
          title: data.title,
          description: data.description || '',
          file_type: data.file_type,
          file_url: data.file_url,
          thumbnail_url: data.thumbnail_url || '',
          category: data.category,
          embed_code: data.embed_code || '',
        });
      }
    } catch (error) {
      console.error('Error loading material:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      file_type: 'video',
      file_url: '',
      thumbnail_url: '',
      category: 'Videos',
      embed_code: '',
    });
    setError('');
    setThumbnailFile(null);
    setThumbnailPreview('');
    setMaterialFile(null);
    setUseUrl(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no puede superar los 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten archivos de imagen');
        return;
      }

      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleMaterialFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedExtensions = ['pdf', 'png', 'jpeg', 'jpg', 'rvt', 'rfa', 'rte', 'dwg', 'docx', 'xlsx', 'pptx', 'zip', 'rar'];
      const fileExt = file.name.split('.').pop()?.toLowerCase();

      if (!fileExt || !allowedExtensions.includes(fileExt)) {
        setError('Formato de archivo no permitido. Extensiones válidas: ' + allowedExtensions.join(', '));
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        setError('El archivo no puede superar los 100MB');
        return;
      }

      // Auto-detect file type based on extension
      let detectedType = formData.file_type;
      if (fileExt === 'pdf') {
        detectedType = 'pdf';
      } else if (['png', 'jpeg', 'jpg'].includes(fileExt)) {
        detectedType = 'image';
      } else {
        detectedType = 'document';
      }

      setFormData({ ...formData, file_type: detectedType });
      setMaterialFile(file);
      setError('');
    }
  };

  const uploadThumbnail = async (): Promise<string | null> => {
    if (!thumbnailFile) return null;

    setUploading(true);
    try {
      const fileExt = thumbnailFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(filePath, thumbnailFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('thumbnails')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const uploadMaterialFile = async (): Promise<string | null> => {
    if (!materialFile) return null;

    try {
      const fileExt = materialFile.name.split('.').pop();
      const fileName = `${profile?.id}/${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('materials')
        .upload(fileName, materialFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('materials')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading material file:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let thumbnailUrl = formData.thumbnail_url;
      let fileUrl = formData.file_url;

      if (thumbnailFile) {
        const uploadedUrl = await uploadThumbnail();
        if (uploadedUrl) {
          thumbnailUrl = uploadedUrl;
        }
      }

      if (materialFile && !useUrl) {
        const uploadedFileUrl = await uploadMaterialFile();
        if (uploadedFileUrl) {
          fileUrl = uploadedFileUrl;
        }
      }

      const materialData = {
        ...formData,
        file_url: fileUrl,
        thumbnail_url: thumbnailUrl,
        created_by: profile?.id,
      };

      if (materialId) {
        const { error } = await supabase
          .from('materials')
          .update(materialData)
          .eq('id', materialId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('materials')
          .insert([materialData]);

        if (error) throw error;
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error saving material:', error);
      setError(error.message || 'Error al guardar el material');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-material"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-2xl w-full border border-white/[0.06]"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 id="modal-title-material" className="text-xl font-bold text-white">
            {materialId ? 'Editar Material' : 'Nuevo Material'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.04] rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Título *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Descripción
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Tipo *
              </label>
              <select
                required
                value={formData.file_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFormData({ ...formData, file_type: newType });
                  setUseUrl(newType === 'video');
                  setMaterialFile(null);
                }}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              >
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
                <option value="document">Documento</option>
                <option value="image">Imagen</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Categoría *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              >
                <option value="Videos">Videos</option>
                <option value="PDFs">PDFs</option>
                <option value="Documentos">Documentos</option>
                <option value="Imágenes">Imágenes</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                {formData.file_type === 'video' ? 'URL del Video * (Vimeo, Drive, YouTube, etc.)' : 'Archivo *'}
              </div>
            </label>

            {formData.file_type === 'video' ? (
              <div className="space-y-4">
                <div>
                  <input
                    type="url"
                    placeholder="https://player.vimeo.com/video/... o https://vimeo.com/..."
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                  />
                  <p className="text-xs text-subtle mt-1">
                    URL del video (opcional si usas código embebido)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Código Embebido de Vimeo (Recomendado para videos privados)
                  </label>
                  <textarea
                    placeholder='<iframe src="https://player.vimeo.com/video/..." ...></iframe>'
                    value={formData.embed_code}
                    onChange={(e) => setFormData({ ...formData, embed_code: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime font-mono text-xs"
                  />
                  <p className="text-xs text-subtle mt-1">
                    💡 Para videos con restricciones de dominio en Vimeo, pega aquí el código iframe completo.
                    Este tendrá prioridad sobre la URL.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 glass-deep hover:bg-cubus-lime/10 border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg text-white/70 hover:text-cubus-lime transition-all">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {materialFile ? materialFile.name : 'Seleccionar archivo'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpeg,.jpg,.rvt,.rfa,.rte,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
                      onChange={handleMaterialFileChange}
                      className="hidden"
                      required={!useUrl && !formData.file_url}
                    />
                  </label>
                  {materialFile && (
                    <button
                      type="button"
                      onClick={() => setMaterialFile(null)}
                      className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all text-sm"
                    >
                      Quitar
                    </button>
                  )}
                </div>
                <div className="text-xs text-subtle mt-1">
                  <p>• Tamaño máximo: 100MB</p>
                  <p>• Formatos: PDF, PNG, JPEG, RVT, RFA, RTE, DWG, DOCX, XLSX, PPTX, ZIP, RAR</p>
                </div>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Miniatura (opcional)
            </label>

            <div className="space-y-3">
              {(thumbnailPreview || formData.thumbnail_url) && (
                <div className="relative w-full h-48 glass-deep rounded-lg overflow-hidden border border-white/[0.08]">
                  <img
                    src={thumbnailPreview || formData.thumbnail_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  {thumbnailPreview && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 bg-cubus-lime text-cubus-bg rounded text-xs font-medium">
                        Nueva imagen
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2 glass-deep hover:bg-cubus-lime/10 border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg text-white/70 hover:text-cubus-lime transition-all">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {thumbnailFile ? thumbnailFile.name : 'Subir Imagen'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {(thumbnailPreview || thumbnailFile) && (
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailFile(null);
                      setThumbnailPreview('');
                    }}
                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all text-sm"
                  >
                    Quitar
                  </button>
                )}
              </div>

              <div className="text-xs text-subtle">
                <p>• Tamaño máximo: 5MB</p>
                <p>• Formatos: JPG, PNG, WebP, GIF</p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/[0.08]"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 glass-surface text-subtle">O usa una URL</span>
                </div>
              </div>

              <input
                type="url"
                placeholder="https://images.pexels.com/..."
                value={formData.thumbnail_url}
                onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                disabled={!!thumbnailFile}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold disabled:opacity-50"
            >
              {uploading
                ? 'Subiendo imagen...'
                : loading
                ? 'Guardando...'
                : materialId
                ? 'Actualizar'
                : 'Crear Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
